import { useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './AddItemMenu.module.css'

interface AddItemMenuProps {
  isOpen: boolean
  onClose: () => void
  anchorElement?: HTMLElement | null
  onAddLink: (url: string) => void
  onUploadFile: () => void
}

export default function AddItemMenu({ isOpen, onClose, anchorElement, onAddLink, onUploadFile }: AddItemMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const [menuPosition, setMenuPosition] = useState({ top: '0', left: '0', right: 'auto' })

  useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect()
      const scrollY = window.scrollY
      const scrollX = window.scrollX
      
      // Responsive menu width based on viewport
      let menuWidth = 280 // default desktop width
      let padding = 16
      
      if (window.innerWidth <= 375) {
        menuWidth = Math.min(240, window.innerWidth - 24)
        padding = 12
      } else if (window.innerWidth <= 480) {
        menuWidth = Math.min(260, window.innerWidth - 32)
        padding = 16
      }
      
      // Calculate horizontal position - default to right-aligned with button
      let left = 'auto'
      let right = `${window.innerWidth - rect.right - scrollX}px`
      
      // Check if menu would go off the left edge of the screen
      if (rect.right - menuWidth < padding) {
        // Position from the left edge instead
        left = `${rect.left + scrollX}px`
        right = 'auto'
        
        // If still doesn't fit, align to left edge of viewport
        if (rect.left + menuWidth > window.innerWidth - padding) {
          left = `${padding}px`
          right = 'auto'
        }
      }
      
      // Final check - if positioned from left and would go off right edge
      if (left !== 'auto' && (rect.left + scrollX + menuWidth) > (window.innerWidth - padding)) {
        left = `${window.innerWidth - menuWidth - padding}px`
        right = 'auto'
      }
      
      setMenuPosition({
        top: `${rect.bottom + scrollY + 8}px`,
        left: left === 'auto' ? 'auto' : `${left}px`,
        right: right
      })
    }
  }, [isOpen, anchorElement])

  useEffect(() => {
    if (isOpen && linkInputRef.current) {
      linkInputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const formData = new FormData(event.target as HTMLFormElement)
    const url = formData.get('link') as string
    if (url.trim()) {
      onAddLink(url.trim())
      onClose()
    }
  }

  if (!isOpen) return null

  const menuContent = (
    <>
      <div className={styles.overlay} aria-hidden="true" />
      <div
        ref={menuRef}
        className={styles.menu}
        style={{
          top: menuPosition.top,
          left: menuPosition.left,
          right: menuPosition.right
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-item-menu-title"
      >
        <div className={styles.menuContent}>
          <form onSubmit={handleSubmit} className={styles.linkForm}>
            <label htmlFor="link-input" className={styles.label}>
              Paste link here
            </label>
            <div className={styles.inputWrapper}>
              <input
                ref={linkInputRef}
                id="link-input"
                name="link"
                type="url"
                placeholder="https://example.com"
                className={styles.input}
                aria-describedby="link-input-description"
              />
              <button
                type="submit"
                className={styles.addButton}
                aria-describedby="add-button-description"
              >
                Add
              </button>
            </div>
          </form>

          <div className={styles.separator} role="separator" aria-hidden="true" />

          <button
            type="button"
            onClick={() => {
              onUploadFile()
              onClose()
            }}
            className={styles.uploadButton}
            aria-describedby="upload-button-description"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10M11.3333 5.33333L8 2M8 2L4.66667 5.33333M8 2V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Upload file
          </button>
        </div>
      </div>
    </>
  )

  return createPortal(menuContent, document.body)
}